import type { CSSProperties, ReactElement } from "react";
import type { Media } from "@bronte/viewer";
import type { ViewerDeviceRuntime } from "../../../types";
import { resolveImageUrlForDevice } from "../../../util/resolveOfflinePackageImageUrl";

export type McqImage = {
  source?: { original?: { url?: string } };
  metadata?: { altText?: string; longDesc?: string };
};

export type McqChoice = {
  choiceLabel: string;
  choiceImage?: McqImage | string;
  correct?: boolean;
  feedback?: string;
  feedbackImage?: McqImage | string;
};

export type EtextMcqPluginConfig = {
  activityLabel?: string;
  activityTitle?: string;
  instructions?: string;
  label?: string;
  questionImage?: McqImage | string;
  buttonPosition?: "left" | "center" | "right";
  allowMultiSelect?: boolean;
  choices?: McqChoice[];
  style?: CSSProperties & { textColor?: string; customCss?: string; cssUrl?: string };
  cssClass?: string;
};

export function parsePluginConfig(raw: unknown): EtextMcqPluginConfig {
  if (raw == null) return {};
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) as EtextMcqPluginConfig;
    } catch (err) {
      console.error("etext-mcq: failed to parse plugin config JSON", err);
      return {};
    }
  }
  return raw as EtextMcqPluginConfig;
}

export function normalizeMcqChoices(rawChoices: McqChoice[]): McqChoice[] {
  return rawChoices.map((c, i) => ({
    ...c,
    choiceLabel:
      c.choiceLabel && String(c.choiceLabel).trim().length > 0
        ? String(c.choiceLabel)
        : `<p dir="auto">Text of choice ${i + 1}</p>`,
    correct: !!c.correct,
  }));
}

function buildFeedbackMediaPayload(src: string, alt: string): Media {
  return {
    mediaType: "Image",
    metadata: {
      src,
      alt: alt || "",
      caption: "",
    },
  };
}

export function McqImageBlock(props: {
  image: McqImage | string | undefined;
  variant: "question" | "choice" | "feedback";
  alt?: string;
  dataId?: string;
  onMediaClick?: (data: Media) => void;
  viewerDevice?: ViewerDeviceRuntime;
}) {
  const { image, variant, dataId, onMediaClick, viewerDevice } = props;
  if (!image || image === "") return null;

  const openInModal = variant === "feedback" && typeof onMediaClick === "function";

  const wrapFeedbackImage = (src: string, alt: string, inner: ReactElement) => {
    if (!openInModal) return inner;
    const payload = buildFeedbackMediaPayload(src, alt);
    return (
      <button
        type="button"
        className="mcq-feedback-image-button"
        aria-label={alt ? `Open image: ${alt}` : "Open image"}
        onClick={() => onMediaClick!(payload)}
      >
        {inner}
      </button>
    );
  };

  if (typeof image === "string") {
    const alt = props.alt ?? "";
    const resolvedSrc = resolveImageUrlForDevice(image, viewerDevice);
    const inner = (
      <img
        data-id={dataId}
        className={`mcq-image mcq-image--${variant}`}
        src={resolvedSrc}
        alt={alt}
      />
    );
    return wrapFeedbackImage(resolvedSrc, alt, inner);
  }

  const url = resolveImageUrlForDevice(image.source?.original?.url, viewerDevice);
  if (!url) return null;
  const longDesc = image.metadata?.longDesc;
  const alt = image.metadata?.altText ?? "";
  const inner = (
    <img data-id={dataId} className={`mcq-image mcq-image--${variant}`} src={url} alt={alt} />
  );
  return (
    <>
      {wrapFeedbackImage(url, alt, inner)}
      {longDesc ? (
        <div
          className="mcq-long-desc"
          data-id={dataId ? `${dataId}-longDesc` : undefined}
          dangerouslySetInnerHTML={{ __html: longDesc }}
        />
      ) : null}
    </>
  );
}

const ANNOTATION_TARGET_SELECTOR =
  ".annotation-text-highlight, .annotation-text-flashcard, .annotation-mathml";

export function isMcqAnnotationTarget(target: EventTarget | null): boolean {
  return Boolean((target as HTMLElement | null)?.closest?.(ANNOTATION_TARGET_SELECTOR));
}

export function correctAnswersSelected(correct: McqChoice[], selected: McqChoice[]): boolean {
  if (correct.length === 0) return false;
  return correct.every((c) => selected.includes(c));
}

export function choiceIsSelected(
  allowMulti: boolean,
  label: string,
  single: string | null,
  multi: string[],
): boolean {
  if (allowMulti) return multi.includes(label);
  return single === label;
}
